import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { PagedList } from "@/components/ListPagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { BRANCHES } from "@/lib/branches";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Printer, Download, MessageCircle, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, parseTermDate, parseTermMonth } from "@/lib/format";
import { printMISReport } from "@/lib/print";

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const bookingDate = (r) => r.lr_date || r.dispatch_date || (r.created_at ? String(r.created_at).slice(0, 10) : "");
const isDelivered = (r) => /deliver|complete|paid/i.test(r.status || "") || !!r.delivery_date || !!r.pod_path;
const custName = (r) => r.consignor || r.customer_name || "Unassigned";

const waLink = (phone, text) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const full = digits.length === 10 ? "91" + digits : digits;
  return `https://wa.me/${full}?text=${encodeURIComponent(text)}`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function MISReport() {
  const [rows, setRows] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [customer, setCustomer] = useState("All");
  const [q, setQ] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [mode, setMode] = useState("pending_delivered");
  const [branch, setBranch] = useState("All");
  const [deliverRow, setDeliverRow] = useState(null);
  const [deliverDate, setDeliverDate] = useState(today());
  const [podFile, setPodFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    Promise.all([api.get("/shipments"), api.get("/invoices")])
      .then(([s, i]) => {
        setRows(s.data);
        setInvoices(i.data || []);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));

  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomersList(r.data || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDeliver = (r) => {
    setDeliverRow(r);
    setDeliverDate(today());
    setPodFile(null);
  };

  const saveDeliver = async () => {
    if (!deliverRow) return;
    if (!deliverDate) return toast.error("Pick a delivery date");
    setSaving(true);
    try {
      await api.post(`/shipments/${deliverRow.id}/mark-delivered`, { delivery_date: deliverDate });
      if (podFile) {
        const fd = new FormData();
        fd.append("file", podFile);
        await api.post(`/consignments/${deliverRow.id}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const lrNo = String(deliverRow.lr_no || deliverRow.consignment_no || "").replace(/^CN[-\s]?/i, "");
      const cust = customersList.find((c) => c.id === deliverRow.customer_id);
      const digits = String(cust?.phone || "").replace(/\D/g, "");
      const waLines = [
        `*DELIVERY CONFIRMATION — Nisha Transport Corporation*`,
        ``,
        `Your consignment has been *DELIVERED* ✔`,
        `LR No.: *${lrNo}*`,
        `Delivered on: ${fmtDate(deliverDate)}`,
        `From: ${deliverRow.origin || "—"}  →  To: ${deliverRow.destination || "—"}`,
        `Consignee: ${deliverRow.consignee || "—"}`,
        deliverRow.vehicle_no ? `Vehicle No.: ${deliverRow.vehicle_no}` : "",
        podFile ? `POD copy received and uploaded.` : "",
        ``,
        `Thank you for your business!`,
        `— Nisha Transport Corporation`,
      ].filter((l) => l !== "");
      const waUrl = `https://wa.me/${digits ? (digits.length === 10 ? "91" + digits : digits) : ""}?text=${encodeURIComponent(waLines.join("\n"))}`;
      toast.success(`LR ${lrNo} marked Delivered${podFile ? " with POD" : ""}`, {
        duration: 10000,
        action: { label: "Share on WhatsApp", onClick: () => window.open(waUrl, "_blank") },
      });
      setDeliverRow(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const customers = useMemo(() => Array.from(new Set((rows || []).map(custName).filter(Boolean))).sort(), [rows]);
  const phoneByName = useMemo(() => {
    const m = {};
    customersList.forEach((c) => { if (c.name && c.phone) m[String(c.name).trim().toLowerCase()] = c.phone; });
    return m;
  }, [customersList]);
  const codeByName = useMemo(() => {
    const m = {};
    customersList.forEach((c) => { if (c.name && c.customer_code) m[String(c.name).trim().toLowerCase()] = c.customer_code; });
    return m;
  }, [customersList]);

  const invByShipment = useMemo(() => {
    const norm = (v) => String(v || "").replace(/^(CN|INV)[-\s]?/i, "").replace(/^0+/, "").trim().toLowerCase();
    const byLr = {};
    const byId = {};
    invoices.forEach((inv) => {
      const ids = [...(inv.shipment_id ? [inv.shipment_id] : []), ...(Array.isArray(inv.shipment_ids) ? inv.shipment_ids : [])];
      ids.forEach((sid) => { if (sid && !byId[sid]) byId[sid] = inv; });
      const lrs = [inv.lr_no, ...(Array.isArray(inv.items) ? inv.items.map((it) => it.lr_no) : [])];
      lrs.forEach((lr) => { const k = norm(lr); if (k && !byLr[k]) byLr[k] = inv; });
    });
    return { byLr, byId, norm };
  }, [invoices]);

  const useRange = !!(fromDate && toDate);
  const inRange = (d) => !useRange || (d >= fromDate && d <= toDate);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows
      .filter((r) => {
        if (customer !== "All" && custName(r) !== customer) return false;
        if (branch !== "All" && (r.office || r.office_name || "") !== branch) return false;
        const needle = q.trim().toLowerCase();
        const tDate = needle ? parseTermDate(needle) : null;
        const tMonth = needle && !tDate ? parseTermMonth(needle) : null;
        if (tDate) {
          const d = (bookingDate(r) || "").slice(0, 10);
          if (d !== tDate) return false;
        } else if (tMonth) {
          const d = (bookingDate(r) || "").slice(0, 7);
          if (d !== tMonth) return false;
        } else if (needle) {
          const name = custName(r);
          const hay = [name, codeByName[String(name).trim().toLowerCase()], String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""), r.destination, r.origin, r.consignee, r.vehicle_no];
          if (!hay.some((x) => String(x || "").toLowerCase().includes(needle))) return false;
        }
        const delivered = isDelivered(r);
        if (pendingOnly && delivered) return false;
        if (mode === "booked") return inRange(bookingDate(r));
        if (!delivered) return true;
        return inRange(r.delivery_date || bookingDate(r));
      })
      .map((r) => ({ ...r, _delivered: isDelivered(r), _inv: invByShipment.byLr[invByShipment.norm(r.lr_no || r.consignment_no)] || invByShipment.byId[r.id] || null }))
      .sort((a, b) => (custName(a) === custName(b) ? (bookingDate(a) < bookingDate(b) ? -1 : 1) : custName(a) < custName(b) ? -1 : 1));
  }, [rows, customer, q, codeByName, pendingOnly, mode, fromDate, toDate, invByShipment, branch]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      const k = custName(r);
      (m[k] = m[k] || { customer: k, code: codeByName[String(k).trim().toLowerCase()] || "", rows: [] }).rows.push(r);
    });
    return Object.values(m).sort((a, b) => (a.customer < b.customer ? -1 : 1));
  }, [filtered, codeByName]);

  const pendingCount = filtered.filter((r) => !r._delivered).length;
  const deliveredCount = filtered.length - pendingCount;
  const totalFreight = filtered.reduce((s, r) => s + (Number(r.freight_amount) || 0), 0);
  const invTotal = (rs) => rs.reduce((s, r) => s + (Number(r.invoice_value) || 0), 0);
  const pkgTotal = (rs) => rs.reduce((s, r) => s + (Number(r.packages) || 0), 0);
  const totalInvValue = invTotal(filtered);
  const totalPkg = pkgTotal(filtered);
  const periodLabel = useRange ? `${fmt(fromDate)} — ${fmt(toDate)}` : "ALL DATES";
  const modeLabel = mode === "booked" ? "Booked in period (current status)" : "All pending + delivered in period";

  const doPrint = (gs) => {
    if (!gs.length) return toast.info("No consignments to print");
    printMISReport(gs, { periodLabel, modeLabel, branchLabel: branch !== "All" ? branch : "" });
  };

  const [morningOpen, setMorningOpen] = useState(false);
  const [waSent, setWaSent] = useState({});
  const pendingGroups = groups.map((g) => ({ ...g, pend: g.rows.filter((r) => !r._delivered) })).filter((g) => g.pend.length);

  const sendPendingWa = (g) => {
    let phone = phoneByName[g.customer.trim().toLowerCase()];
    if (!phone) {
      phone = window.prompt(`No phone found in Customer Master for "${g.customer}". Enter WhatsApp number:`) || "";
      if (!phone.trim()) return;
    }
    const line = (r) => `• LR ${String(r.lr_no || r.consignment_no || "—").replace(/^CN[-\s]?/i, "")} | ${r.origin || "?"}→${r.destination || "?"} | booked ${fmt(bookingDate(r))}`;
    const shown = g.pend.slice(0, 30).map(line).join("\n");
    const more = g.pend.length > 30 ? `\n…and ${g.pend.length - 30} more` : "";
    const text = `*Good Morning! Pending Consignment Update — ${fmt(new Date().toISOString().slice(0, 10))}*\nCustomer: ${g.customer}\nPending consignments: ${g.pend.length}\n\n${shown}${more}\n\nWe will update you as each one is delivered.\n— Nisha Transport Corporation`;
    window.open(waLink(phone, text), "_blank");
    setWaSent((s) => ({ ...s, [g.customer]: true }));
  };

  const sendWhatsApp = (g) => {
    let phone = phoneByName[g.customer.trim().toLowerCase()];
    if (!phone) {
      phone = window.prompt(`No phone found in Customer Master for "${g.customer}". Enter WhatsApp number:`) || "";
      if (!phone.trim()) return;
    }
    const pend = g.rows.filter((r) => !r._delivered);
    const dlv = g.rows.filter((r) => r._delivered);
    const line = (r) => `• LR ${String(r.lr_no || r.consignment_no || "—").replace(/^CN[-\s]?/i, "")} | ${r.origin || "?"}→${r.destination || "?"}${r._delivered ? ` | DELIVERED ${r.delivery_date ? fmt(r.delivery_date) : ""}` : " | PENDING"}`;
    const shown = g.rows.slice(0, 30).map(line).join("\n");
    const more = g.rows.length > 30 ? `\n…and ${g.rows.length - 30} more` : "";
    const text = `*Daily MIS Report — ${periodLabel}*\nCustomer: ${g.customer}\nPending: ${pend.length} | Delivered: ${dlv.length}\n\n${shown}${more}\n\n— Nisha Transport Corporation`;
    window.open(waLink(phone, text), "_blank");
  };

  const exportCsv = () => {
    const header = "Sl,Branch,Customer,LR No.,LR Date,From,Destination,Consignee Name,Basis,Vehicle No.,Vehicle Type,PKG,Invoice No.,Invoice Date,Invoice Value,Freight Amount,Delivery Date,POD,Status";
    const csv = [
      header,
      ...filtered.map((r, i) =>
        [i + 1, r.office || r.office_name || "", custName(r), String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""), bookingDate(r), r.origin, r.destination, r.consignee, r.basis || "", r.vehicle_no, r.vehicle_type || "", r.packages || "", r.invoice_no || "", r.invoice_date || "", r.invoice_value ?? "", r.freight_amount, r.delivery_date || "", r.pod_path ? "Uploaded" : "Pending", r._delivered ? "Delivered" : "Pending"]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
      `"","","","","","","","","","","","","TOTAL","${totalInvValue}","${totalFreight}","","",""`,
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `mis-report-${branch !== "All" ? branch.toLowerCase().replace(/\s+/g, "-") + "-" : ""}${fromDate || "all"}_${toDate || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="mis-report-page">
      <PageHeader
        title="MIS Report"
        subtitle="Daily customer-wise consignment status — pending & delivered."
        action={
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1.5">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <Input
                  className="w-[200px] pl-8 pr-7"
                  placeholder="Customer / code / LR"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="mis-search"
                />
                {q && (
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setQ("")} data-testid="mis-search-clear">
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Branch</Label>
              <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger className="w-[150px]" data-testid="mis-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Branches</SelectItem>
                  {BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Customer</Label>
              <Select value={customer} onValueChange={setCustomer}>
                <SelectTrigger className="w-[180px]" data-testid="mis-customer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Customers</SelectItem>
                  {customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">From</Label>
              <Input type="date" className="w-[140px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="mis-from" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">To</Label>
              <Input type="date" className="w-[140px]" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="mis-to" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Report Basis</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="w-[240px]" data-testid="mis-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_delivered">All pending + delivered in period</SelectItem>
                  <SelectItem value="booked">Booked in period (current status)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Pending Only</Label>
              <div className="flex h-10 items-center">
                <Switch checked={pendingOnly} onCheckedChange={setPendingOnly} data-testid="mis-pending-only" />
              </div>
            </div>
            <Button variant="outline" onClick={exportCsv} data-testid="mis-export-btn">
              <Download className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" onClick={() => { setWaSent({}); setMorningOpen(true); }} data-testid="mis-morning-wa-btn">
              <MessageCircle className="mr-2 h-4 w-4" /> Morning WhatsApp{pendingGroups.length ? ` (${pendingGroups.length})` : ""}
            </Button>
            <Button onClick={() => doPrint(groups)} data-testid="mis-print-all-btn">
              <Printer className="mr-2 h-4 w-4" /> Print{groups.length > 1 ? " All" : ""}
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap gap-4">
        <div className="rounded-lg border border-border bg-card px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Consignments — {periodLabel}</div>
          <div className="font-display text-2xl font-bold" data-testid="mis-count">{rows ? filtered.length : <Skeleton className="h-7 w-12" />}</div>
        </div>
        <div className="rounded-lg border border-border bg-card px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Pending / Delivered</div>
          <div className="font-display text-2xl font-bold" data-testid="mis-status-split">
            <span className="text-amber-600">{pendingCount}</span> / <span className="text-emerald-600">{deliveredCount}</span>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total Invoice Value</div>
          <div className="font-display text-2xl font-bold" data-testid="mis-inv-value">{rows ? inr(totalInvValue) : <Skeleton className="h-7 w-28" />}</div>
        </div>
        <div className="rounded-lg border border-border bg-card px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total Freight</div>
          <div className="font-display text-2xl font-bold text-primary" data-testid="mis-freight">{rows ? inr(totalFreight) : <Skeleton className="h-7 w-28" />}</div>
        </div>
        <div className="rounded-lg border border-border bg-card px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total PKG</div>
          <div className="font-display text-2xl font-bold" data-testid="mis-pkg-total">{rows ? totalPkg : <Skeleton className="h-7 w-12" />}</div>
        </div>
        <div className="rounded-lg border border-border bg-card px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Customers</div>
          <div className="font-display text-2xl font-bold" data-testid="mis-customer-count">{groups.length}</div>
        </div>
      </div>

      {rows === null && (
        <TableShell><div className="space-y-2 p-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div></TableShell>
      )}
      {rows && groups.length === 0 && (
        <TableShell><div className="py-12 text-center text-muted-foreground">No consignments match the current filters.</div></TableShell>
      )}

      {groups.map((g, gi) => {
        const pend = g.rows.filter((r) => !r._delivered).length;
        const sub = g.rows.reduce((s, r) => s + (Number(r.freight_amount) || 0), 0);
        const subInv = invTotal(g.rows);
        const subPkg = pkgTotal(g.rows);
        return (
          <div key={g.customer} className="mb-6" data-testid="mis-customer-group">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-slate-50 px-4 py-2.5">
              <div className="flex items-center gap-3">
                <span className="font-display text-sm font-bold uppercase tracking-wide">{g.customer}</span>
                {codeByName[String(g.customer).trim().toLowerCase()] && (
                  <Badge variant="outline" className="border-indigo-300 bg-indigo-50 font-bold text-indigo-700" data-testid="mis-customer-code">
                    Code: {codeByName[String(g.customer).trim().toLowerCase()]}
                  </Badge>
                )}
                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">Pending {pend}</Badge>
                <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">Delivered {g.rows.length - pend}</Badge>
                <Badge variant="outline" className="border-sky-300 bg-sky-50 text-sky-700" data-testid="mis-group-pkg-badge">PKG {subPkg}</Badge>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => sendWhatsApp(g)} data-testid="mis-whatsapp-btn">
                  <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                </Button>
                <Button size="sm" variant="secondary" onClick={() => doPrint([g])} data-testid="mis-print-group-btn">
                  <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
                </Button>
              </div>
            </div>
            <PagedList items={g.rows} noun="consignments" testPrefix={`mis-group-${gi}`}>
              {(pageRows, offset) => (
            <TableShell>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    {["SL No.", "Branch", "LR No.", "LR Date", "From", "Destination", "Consignee Name", "Basis", "Vehicle No.", "Vehicle Type", "PKG", "Invoice No.", "Invoice Date", "Invoice Value", "Freight Amount", "Delivery Date", "POD", "Status", "Action"].map((h) => (
                      <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${h === "Freight Amount" || h === "Invoice Value" ? "text-right" : ""}`}>{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((r, i) => (
                    <TableRow key={r.id} data-testid="mis-row">
                      <TableCell className="text-sm">{offset + i + 1}</TableCell>
                      <TableCell className="text-sm" data-testid="mis-branch-cell">{r.office || r.office_name || "—"}</TableCell>
                      <TableCell className="text-sm font-medium">{String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "") || "—"}</TableCell>
                      <TableCell className="text-sm">{fmt(bookingDate(r))}</TableCell>
                      <TableCell className="text-sm">{r.origin || "—"}</TableCell>
                      <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                      <TableCell className="text-sm">{r.consignee || "—"}</TableCell>
                      <TableCell className="text-sm" data-testid="mis-basis-cell">{r.basis || "—"}</TableCell>
                      <TableCell className="text-sm">{r.vehicle_no || "—"}</TableCell>
                      <TableCell className="text-sm" data-testid="mis-vehicle-type-cell">{r.vehicle_type || "—"}</TableCell>
                      <TableCell className="text-sm" data-testid="mis-pkg-cell">{r.packages || "—"}</TableCell>
                      <TableCell className="text-sm font-medium" data-testid="mis-invoice-no-cell">{r.invoice_no || "—"}</TableCell>
                      <TableCell className="text-sm">{r.invoice_date ? fmt(r.invoice_date) : "—"}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{Number(r.invoice_value) ? inr(r.invoice_value) : "—"}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{inr(r.freight_amount)}</TableCell>
                      <TableCell className="text-sm">{r.delivery_date ? fmt(r.delivery_date) : "—"}</TableCell>
                      <TableCell className="text-sm">
                        {r.pod_path ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">Uploaded</Badge> : <span className="text-muted-foreground">Pending</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r._delivered ? <Badge className="bg-emerald-600">Delivered</Badge> : <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">Pending</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r._delivered ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-emerald-700" onClick={() => openDeliver(r)} data-testid="mis-mark-delivered-btn">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Mark Delivered
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-100 font-bold">
                    <TableCell colSpan={10} className="text-right text-sm">TOTAL</TableCell>
                    <TableCell className="text-sm tabular-nums" data-testid="mis-group-pkg-total">{subPkg}</TableCell>
                    <TableCell colSpan={2}></TableCell>
                    <TableCell className="text-right text-sm tabular-nums" data-testid="mis-group-inv-total">{inr(subInv)}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-primary" data-testid="mis-group-freight-total">{inr(sub)}</TableCell>
                    <TableCell colSpan={4}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableShell>
              )}
            </PagedList>
          </div>
        );
      })}

      <Dialog open={morningOpen} onOpenChange={setMorningOpen}>
        <DialogContent className="max-w-lg" data-testid="mis-morning-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Morning MIS WhatsApp</DialogTitle>
            <DialogDescription>Send each customer their pending-consignment list. Tap Send, press send in WhatsApp, come back for the next one.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {pendingGroups.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No customers with pending consignments. 🎉</p>}
            {pendingGroups.map((g) => (
              <div key={g.customer} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2" data-testid="mis-morning-row">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{g.customer}</div>
                  <div className="text-xs text-muted-foreground">
                    {g.pend.length} pending · {phoneByName[g.customer.trim().toLowerCase()] ? phoneByName[g.customer.trim().toLowerCase()] : "no phone — will ask"}
                  </div>
                </div>
                {waSent[g.customer] ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="mis-morning-sent"><CheckCircle2 className="h-4 w-4" /> Opened</span>
                ) : null}
                <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={() => sendPendingWa(g)} data-testid="mis-morning-send-btn">
                  <MessageCircle className="mr-1 h-3.5 w-3.5" /> Send
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deliverRow} onOpenChange={(o) => !o && setDeliverRow(null)}>
        <DialogContent className="max-w-md" data-testid="mis-deliver-dialog">
          <DialogHeader>
            <DialogTitle>
              Mark Delivered — LR {String(deliverRow?.lr_no || deliverRow?.consignment_no || "").replace(/^CN[-\s]?/i, "")}
            </DialogTitle>
            <DialogDescription>
              {deliverRow?.origin || "—"} → {deliverRow?.destination || "—"} · {custName(deliverRow || {})}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-1.5">
              <Label>Delivery Date</Label>
              <Input type="date" value={deliverDate} onChange={(e) => setDeliverDate(e.target.value)} data-testid="mis-deliver-date" />
            </div>
            <div className="grid gap-1.5">
              <Label>Attach POD (optional)</Label>
              <Input type="file" accept="image/*,.pdf" onChange={(e) => setPodFile(e.target.files?.[0] || null)} data-testid="mis-deliver-pod" />
              <p className="text-xs text-muted-foreground">JPG/PNG/WEBP/PDF up to 10MB. You can also upload later from POD Upload.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeliverRow(null)} data-testid="mis-deliver-cancel">Cancel</Button>
            <Button onClick={saveDeliver} disabled={saving} data-testid="mis-deliver-save">
              {saving ? "Saving…" : "Mark Delivered"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
